import React, { useEffect } from 'react';
import { getAppVersion, sendVersionInfoToNative } from './services/appVersionApi';
import { Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import ConfirmEmailChangePage from './pages/auth/ConfirmEmailChangePage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import TermsOfServicePage from './pages/legal/TermsOfServicePage';
import TermsOfUsePage from './pages/legal/TermsOfUsePage';
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import AccountDeletionPage from './pages/legal/AccountDeletionPage';
import QuickStartGuidePage from './pages/legal/QuickStartGuidePage';
import DevicesPage from './pages/devices/DevicesPage';
import AddDevicePage from './pages/devices/AddDevicePage';
import ManuallyAddDevicePage from './pages/devices/ManuallyAddDevicePage';
import BulkImportDevicesPage from './pages/devices/BulkImportDevicesPage';
import DeviceDetailPage from './pages/devices/DeviceDetailPage';
import NewDeviceDetailPage from './pages/devices/NewDeviceDetailPage';
import AlertsPage from './pages/alerts/AlertsPage';
import NewAlertsPage from './pages/alerts/NewAlertsPage';
import ReportsPage from './pages/reports/ReportsPage';
import SettingsPage from './pages/dashboard/SettingsPage';
import ShareLinkPage from './pages/share/ShareLinkPage';
import PublicShareView from './pages/share/PublicShareView';
import UserManagementPage from './pages/userManagement/UserManagementPage';
import AddUserPage from './pages/userManagement/AddUserPage';
import HelpSupportPage from './pages/helpSupport/HelpSupportPage';
import TicketDetailPage from './pages/helpSupport/TicketDetailPage';
import ProtectedRoute from './components/ProtectedRoute';
import AuthInterceptor from './components/AuthInterceptor';
import { DevicesProvider } from './contexts/DevicesContext';
import { TicketProvider } from './contexts/TicketContext';
import { UserSelectedProvider } from './contexts/UserSelectedContext';
import { AuthProvider } from './contexts/AuthContext';
import { UserManagementProvider } from './contexts/UserManagementContext';

const App: React.FC = () => {
  // Send app version info to React Native WebView on load (only runs inside mobile app)
  useEffect(() => {
    const isInsideMobileApp = !!(window as any).ReactNativeWebView;
    if (!isInsideMobileApp) return;

    const platform = /android/i.test(navigator.userAgent) ? 'android' : 'ios';
    const appName = (window as any).NATIVE_APP_NAME || 'NA';
    getAppVersion(appName, platform).then((versionInfo) => {
      if (versionInfo) {
        sendVersionInfoToNative(versionInfo);
      }
    });
  }, []);

  return (
    <AuthProvider>
      <DevicesProvider>
        <TicketProvider>
        <UserManagementProvider>  
        <UserSelectedProvider>
          <AuthInterceptor>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} /> 
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/confirm-email-change" element={<ConfirmEmailChangePage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/terms-of-use" element={<TermsOfUsePage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route
                path="/devices"
                element={
                  <ProtectedRoute>
                    <DevicesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/add-device"
                element={
                  <ProtectedRoute>
                    <AddDevicePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manually-add-device"
                element={
                  <ProtectedRoute>
                    <ManuallyAddDevicePage />
                  </ProtectedRoute>
                }
              />
              {/* Bulk Import - Hidden as per requirement */}
              <Route
                path="/bulk-import-devices"
                element={
                  <ProtectedRoute>
                    <BulkImportDevicesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/edit-device/:id"
                element={
                  <ProtectedRoute>
                    <ManuallyAddDevicePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/devices/:id"
                element={
                  <ProtectedRoute>
                    <DeviceDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/new-device-design/:id"
                element={
                  <ProtectedRoute>
                    <NewDeviceDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/new-device-design"
                element={
                  <ProtectedRoute>
                    <NewDeviceDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/alerts"
                element={
                  <ProtectedRoute>
                    <AlertsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/new-alerts"
                element={
                  <ProtectedRoute>
                    <NewAlertsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <ProtectedRoute>
                    <ReportsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/share"
                element={
                  <ProtectedRoute>
                    <ShareLinkPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/user-management"
                element={
                  <ProtectedRoute>
                    <UserManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/add-user"
                element={
                  <ProtectedRoute>
                    <AddUserPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/edit-user/:id"
                element={
                  <ProtectedRoute>
                    <AddUserPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/help-support"
                element={
                  <ProtectedRoute>
                    <HelpSupportPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/help-support/:id"
                element={
                  <ProtectedRoute>
                    <TicketDetailPage />
                  </ProtectedRoute>
                }
              />
              {/* Public Share Link Routes - No Authentication Required */}
              <Route path="/public/share/:url" element={<PublicShareView />} />
              <Route path="/share/:url" element={<PublicShareView />} />
              <Route path="/" element={<LoginPage />} />
              {/* Public pages that should not be caught by share link route */}
              <Route path="/account-deletion" element={<AccountDeletionPage />} />
              <Route path="/quick-start-guide" element={<QuickStartGuidePage />} />
              <Route path="/user-manual" element={<QuickStartGuidePage />} />
              {/* Catch-all route for direct share links - MUST BE LAST */}
              <Route path="/:url" element={<PublicShareView />} />
              {/* Any nested/unknown path (e.g. /foo/bar/baz) → home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AuthInterceptor>
        </UserSelectedProvider>
        </UserManagementProvider>
        </TicketProvider>
      </DevicesProvider>
    </AuthProvider>
  );
};

export default App;
